import { useRef, useState } from "react";
import { X, ChevronDown, ChevronUp, GripVertical, Minus, Plus, ArrowUp, ArrowDown, Copy, SeparatorHorizontal } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CATEGORY_STYLES, formatTime } from "@/lib/session";
import { toast } from "sonner";

export function SegmentCard({ segment, index, start, end, total, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, onDragStart, onDragOver, onDrop, dragging }) {
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef(null);
  const style = CATEGORY_STYLES[segment.category] || CATEGORY_STYLES.Activities;
  const breakCount = (segment.body || "").split("\n").filter((l) => /^\s*(-{3,}|\[break\])\s*$/i.test(l)).length;

  const insertSlideBreak = () => {
    const el = bodyRef.current;
    const body = segment.body || "";
    const pos = el ? el.selectionStart : body.length;
    const before = body.slice(0, pos).replace(/\s+$/, "");
    const after = body.slice(pos).replace(/^\s+/, "");
    if (!before || !after) {
      toast.info("Place the cursor in the prayer text where the slide should split, then click again.");
      return;
    }
    onUpdate(segment.id, { body: `${before}\n\n---\n\n${after}` });
    toast.success("Slide break added");
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      className={`group relative bg-white dark:bg-[var(--brand-card-dark)] border ${dragging ? "opacity-40" : ""} border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-4 transition-shadow hover:shadow-md`}
      data-testid={`segment-card-${index}`}
    >
      <div className="flex items-start gap-3 sm:contents">
        <div className="flex flex-col items-center pt-1 gap-1 shrink-0">
          <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground/50" data-testid={`segment-drag-handle-${index}`} />
          <Button size="icon" variant="ghost" className="h-5 w-5 disabled:opacity-20" disabled={index === 0} onClick={() => onMoveUp(index)} title="Move up" data-testid={`segment-move-up-${index}`}>
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-5 w-5 disabled:opacity-20" disabled={index === total - 1} onClick={() => onMoveDown(index)} title="Move down" data-testid={`segment-move-down-${index}`}>
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="w-auto sm:w-28 shrink-0 whitespace-nowrap">
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => onUpdate(segment.id, { duration_minutes: Math.max(1, segment.duration_minutes - 1) })} data-testid={`segment-duration-decrease-${index}`}>
              <Minus className="h-3 w-3" />
            </Button>
            <span className="font-mono text-xs font-semibold whitespace-nowrap" data-testid={`segment-duration-${index}`}>{segment.duration_minutes} min</span>
            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => onUpdate(segment.id, { duration_minutes: segment.duration_minutes + 1 })} data-testid={`segment-duration-increase-${index}`}>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          <p className="font-mono text-[11px] text-muted-foreground mt-1 whitespace-nowrap" data-testid={`segment-time-range-${index}`}>
            {formatTime(start)} &ndash; {formatTime(end)}
          </p>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${style.badge}`} data-testid={`segment-category-badge-${index}`}>
            {segment.category}
          </span>
          <div className="flex sm:hidden items-center gap-0.5 shrink-0">
            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" title="Duplicate segment" onClick={() => onDuplicate(segment.id)} data-testid={`segment-duplicate-button-mobile-${index}`}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" title="Delete segment" onClick={() => onDelete(segment.id)} data-testid={`segment-delete-button-mobile-${index}`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <Input
              value={segment.title}
              onChange={(e) => onUpdate(segment.id, { title: e.target.value })}
              className="h-auto text-base md:text-base font-semibold bg-[var(--brand-bg)] dark:bg-white/5 border border-stone-400 dark:border-[var(--brand-border-dark)] rounded-md px-2.5 py-1.5 focus-visible:ring-1 focus-visible:ring-[var(--brand-crimson)] focus-visible:border-[var(--brand-crimson)]"
              data-testid={`segment-title-input-${index}`}
            />
          </div>
          <div className="hidden sm:flex items-center gap-0.5 shrink-0 opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" title="Duplicate segment" onClick={() => onDuplicate(segment.id)} data-testid={`segment-duplicate-button-${index}`}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" title="Delete segment" onClick={() => onDelete(segment.id)} data-testid={`segment-delete-button-${index}`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-end mt-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button
            onClick={insertSlideBreak}
            className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-[var(--brand-crimson)]"
            title="Force a new slide at the cursor"
            data-testid={`segment-slide-break-button-${index}`}
          >
            <SeparatorHorizontal className="h-3 w-3" /> Split slide here
          </button>
        </div>
        <Textarea
          ref={bodyRef}
          value={segment.body}
          onChange={(e) => onUpdate(segment.id, { body: e.target.value })}
          rows={2}
          className="resize-none bg-[var(--brand-bg)] dark:bg-white/5 border border-stone-400 dark:border-[var(--brand-border-dark)] rounded-md px-2.5 py-1.5 focus-visible:ring-1 focus-visible:ring-[var(--brand-crimson)] focus-visible:border-[var(--brand-crimson)] text-sm mt-2"
          placeholder="Editable copy text OR can be from a pre-set text"
          data-testid={`segment-body-textarea-${index}`}
        />
        {breakCount > 0 && (
          <div className="flex items-center gap-2 mt-1" data-testid={`segment-slide-break-marker-${index}`}>
            <span className="flex-1 border-t border-dashed border-[var(--brand-crimson)]/50" />
            <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--brand-crimson)] dark:text-red-400">
              {breakCount} slide break{breakCount > 1 ? "s" : ""}
            </span>
            <span className="flex-1 border-t border-dashed border-[var(--brand-crimson)]/50" />
          </div>
        )}
        <button
          className="flex items-center gap-1 text-xs font-medium text-[var(--brand-crimson)] dark:text-red-400 mt-1"
          onClick={() => setExpanded(!expanded)}
          data-testid={`segment-details-toggle-${index}`}
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          Details
        </button>
        {expanded && (
          <Textarea
            value={segment.details}
            onChange={(e) => onUpdate(segment.id, { details: e.target.value })}
            rows={2}
            placeholder="Leader cues, scripture references, or notes..."
            className="mt-2 text-sm bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]"
            data-testid={`segment-details-textarea-${index}`}
          />
        )}
      </div>
    </div>
  );
}
